import React, { useState, useEffect } from 'react';
import { Layout, Steps, Card, message, ConfigProvider, Alert } from 'antd';
import enUS from 'antd/locale/en_US';
import { FileOutlined, DatabaseOutlined, LinkOutlined, SendOutlined, BugOutlined } from '@ant-design/icons';
import FileUpload from './FileUpload';
import TemplateSelection from './TemplateSelection';
import ColumnMapping from './ColumnMapping';
import FileProcessing from './FileProcessing';
import { api } from '../services/api';
import '../index.css';

const { Header, Content } = Layout;
const { Step } = Steps;

function DevApp() {
    const [currentStep, setCurrentStep] = useState(0);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [fileColumns, setFileColumns] = useState([]);
    const [templates, setTemplates] = useState({});
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [columnMappings, setColumnMappings] = useState([]);

    // Load templates on component mount (no auth required in dev mode)
    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        try {
            const response = await api.get('/templates');
            setTemplates(response.data.templates);
        } catch (error) {
            message.error('Failed to load templates configuration');
        }
    };

    const handleFileUpload = (file, preview, columns) => {
        setUploadedFile(file);
        setFilePreview(preview);
        setFileColumns(columns);
        setCurrentStep(1);
    };

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setCurrentStep(2);
    };

    const handleMappingComplete = (mappings) => {
        setColumnMappings(mappings);
        setCurrentStep(3);
    };

    const handleProcessingComplete = (outputFile) => {
        // Don't set processedFile to avoid component re-render
    };

    const handleReset = () => {
        setCurrentStep(0);
        setUploadedFile(null);
        setFilePreview(null);
        setFileColumns([]);
        setSelectedTemplate(null);
        setColumnMappings([]);
    };

    const steps = [
        {
            title: 'Upload File',
            icon: <FileOutlined />,
        },
        {
            title: 'Select Template',
            icon: <DatabaseOutlined />,
        },
        {
            title: 'Map Columns',
            icon: <LinkOutlined />,
        },
        {
            title: 'Process & Send',
            icon: <SendOutlined />,
        },
    ];

    return (
        <ConfigProvider
            locale={enUS}
            theme={{
                token: {
                    colorPrimary: '#1890ff',
                },
            }}
        >
            <Layout style={{ minHeight: '100vh' }}>
                <Header style={{
                    background: '#fff',
                    padding: '0 50px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <BugOutlined style={{ fontSize: 32, color: '#52c41a', marginRight: 16 }} />
                        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
                            File Uploader (Dev Mode)
                        </h1>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Alert
                            message="Development Mode"
                            description="Authentication disabled"
                            type="warning"
                            showIcon
                            style={{ marginRight: 16 }}
                        />
                        <span style={{ color: '#666' }}>
                            User: dev_user | Tenant: dev-tenant
                        </span>
                    </div>
                </Header>

                <Content style={{ padding: '50px', background: '#f0f2f5' }}>
                    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                        <Card style={{ marginBottom: 24 }}>
                            <Steps current={currentStep} style={{ marginBottom: 24 }}>
                                {steps.map((step, index) => (
                                    <Step
                                        key={index}
                                        title={step.title}
                                        icon={step.icon}
                                    />
                                ))}
                            </Steps>
                        </Card>

                        <Card>
                            {currentStep === 0 && (
                                <FileUpload
                                    onFileUpload={handleFileUpload}
                                    filePreview={filePreview}
                                    fileColumns={fileColumns}
                                />
                            )}

                            {currentStep === 1 && (
                                <TemplateSelection
                                    templates={templates}
                                    selectedTemplate={selectedTemplate}
                                    onTemplateSelect={handleTemplateSelect}
                                />
                            )}

                            {currentStep === 2 && (
                                <ColumnMapping
                                    selectedTemplate={selectedTemplate}
                                    fileColumns={fileColumns}
                                    onMappingComplete={handleMappingComplete}
                                />
                            )}

                            {currentStep === 3 && (
                                <FileProcessing
                                    uploadedFile={uploadedFile}
                                    selectedTemplate={selectedTemplate}
                                    columnMappings={columnMappings}
                                    onProcessingComplete={handleProcessingComplete}
                                    onReset={handleReset}
                                />
                            )}
                        </Card>
                    </div>
                </Content>
            </Layout>
        </ConfigProvider>
    );
}

export default DevApp;
